import { Draft, createReducer } from '@reduxjs/toolkit';
import * as thunkActions from 'thunk';
import * as actions from 'actionCreators';
import {
  ForceUpdateResponseType,
  FetchToIntegrationServerResponseType,
  TeamsHistoricalUtilization,
} from 'ReportsModule/types';
import * as Types from 'types';
import keyBy from 'lodash/keyBy';
import { PromiseAllSettledStatus } from 'services/constants';
import { notificationService } from 'services/notificationService';

type InitialStateType = {
  teams: Types.GenericTeamInterface[];
  teamsInfoMapping: Record<number, Types.GenericTeamInterface>;
  super_admins: Array<Types.SuperAdminInterface>;
  fetchToIntegrationServerResponse: {
    agent: FetchToIntegrationServerResponseType;
    backend: FetchToIntegrationServerResponseType;
  };
  //
  teams_historical_utilization: TeamsHistoricalUtilization;
  teams_historical_utilization_order: number[];
  //
  forceUpdateResponse: ForceUpdateResponseType;
  requestStatusesAndErrors: {
    [x: string]: {
      isRequesting: any;
      error: any;
    };
  };
};

const initialState: InitialStateType = {
  teams: [],
  teamsInfoMapping: {},
  super_admins: [],
  teams_historical_utilization: {},
  teams_historical_utilization_order: [],
  fetchToIntegrationServerResponse: {
    agent: {
      statusCode: 0,
      data: '',
    },
    backend: {
      statusCode: 0,
      data: '',
    },
  },
  forceUpdateResponse: {
    data: '',
    statusCode: 0,
  },
  requestStatusesAndErrors: {
    // Example
    [thunkActions.fetchDataToIntegrationServer.typePrefix]: {
      isRequesting: {
        agent: false,
        backend: false,
      },
      error: {
        agent: {},
        backend: {},
      },
    },
  },
};

export default createReducer(initialState, (builder) => {
  /* ---------------------------- Non Async Request --------------------------- */
  // To remove when refactor reports page
  builder.addCase(actions.updateTeamsInfoMapping, (state, { payload }) => {
    state.teamsInfoMapping = {
      ...(state?.teamsInfoMapping || {}),
      ...payload,
    };
  });

  /* ------------------------------- Fetch Teams ------------------------------ */
  builder.addCase(thunkActions.fetchReportsTeams.pending, (state, { meta }) => {
    state.requestStatusesAndErrors[thunkActions.fetchReportsTeams.typePrefix] = {
      isRequesting: true,
      error: {},
    };
  });
  builder.addCase(thunkActions.fetchReportsTeams.fulfilled, (state, { meta, payload }) => {
    state.requestStatusesAndErrors[thunkActions.fetchReportsTeams.typePrefix] = {
      isRequesting: false,
      error: {},
    };
    const { isInitialFetch, teams = [] } = payload;
    state.teams = isInitialFetch
      ? [...teams]
      : [...(state.teams as Types.GenericTeamInterface[]), ...teams];
    state.teamsInfoMapping = {
      ...state.teamsInfoMapping,
      ...keyBy(teams, 'id'),
    };
  });
  builder.addCase(thunkActions.fetchReportsTeams.rejected, (state, { meta, error }) => {
    state.requestStatusesAndErrors[thunkActions.fetchReportsTeams.typePrefix] = {
      isRequesting: false,
      error,
    };
    state = {
      ...state,
      teams: [],
    };
  });

  /* ------------------------------- Fetch Data to IS ------------------------------ */
  builder.addCase(thunkActions.fetchDataToIntegrationServer.pending, (state, { meta }) => {
    const {
      arg: { fetchFrom },
    } = meta;
    const requestStatusByFetchFrom =
      state.requestStatusesAndErrors[thunkActions.fetchDataToIntegrationServer.typePrefix]
        .isRequesting;
    const errorByFetchFrom =
      state.requestStatusesAndErrors[thunkActions.fetchDataToIntegrationServer.typePrefix]
        .isRequesting;

    state.requestStatusesAndErrors[thunkActions.fetchDataToIntegrationServer.typePrefix] = {
      isRequesting: {
        ...requestStatusByFetchFrom,
        [fetchFrom]: true,
      },
      error: {
        ...errorByFetchFrom,
        [fetchFrom]: {},
      },
    };
  });
  builder.addCase(
    thunkActions.fetchDataToIntegrationServer.fulfilled,
    (state, { meta, payload }) => {
      const {
        arg: { fetchFrom },
      } = meta;
      const requestStatusByFetchFrom =
        state.requestStatusesAndErrors[thunkActions.fetchDataToIntegrationServer.typePrefix]
          .isRequesting;
      const errorByFetchFrom =
        state.requestStatusesAndErrors[thunkActions.fetchDataToIntegrationServer.typePrefix]
          .isRequesting;

      state.requestStatusesAndErrors[thunkActions.fetchDataToIntegrationServer.typePrefix] = {
        isRequesting: {
          ...requestStatusByFetchFrom,
          [fetchFrom]: false,
        },
        error: {
          ...errorByFetchFrom,
          [fetchFrom]: {},
        },
      };

      state.fetchToIntegrationServerResponse = {
        ...state.fetchToIntegrationServerResponse,
        [fetchFrom]: {
          statusCode: payload.status,
          data: payload.data,
        },
      };
    },
  );
  /* ------------------------------- IS Force Update ------------------------------ */
  builder.addCase(thunkActions.integrationForceUpdate.pending, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.integrationForceUpdate.typePrefix] = {
      isRequesting: true,
      error: {},
    };
  });
  builder.addCase(thunkActions.integrationForceUpdate.fulfilled, (state, { meta, payload }) => {
    state.requestStatusesAndErrors[thunkActions.integrationForceUpdate.typePrefix] = {
      isRequesting: false,
      error: {},
    };

    state.forceUpdateResponse = {
      statusCode: payload.status,
      data: payload.data,
    };
  });
  /* ------------------------------- Super Admin ------------------------------ */
  // FETCH
  builder.addCase(thunkActions.fetchSuperAdmins.pending, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.fetchSuperAdmins.typePrefix] = {
      isRequesting: true,
      error: {},
    };
  });
  builder.addCase(thunkActions.fetchSuperAdmins.fulfilled, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.fetchSuperAdmins.typePrefix] = {
      isRequesting: false,
      error: {},
    };
    state.super_admins = action.payload;
  });
  builder.addCase(thunkActions.fetchSuperAdmins.rejected, (state, action) => {
    state.requestStatusesAndErrors[thunkActions.fetchSuperAdmins.typePrefix] = {
      isRequesting: false,
      error: action.error,
    };
    state.super_admins = [];
  });

  /* ---------------------------- Deactivate Teams ---------------------------- */
  // Use string matcher to avoid circular dependency with thunks
  builder.addMatcher(
    (action): action is { type: string; payload: any; meta: any } =>
      action.type === 'deactivateTeams/fulfilled',
    (state, action) => {
      const { payload, meta } = action;
      const {
        arg: { skipStateUpdate },
      } = meta;

      if (!skipStateUpdate) {
        const idsSource: number[] =
          meta.arg.teamTargets?.map((t: { teamId: number }) => t.teamId) ?? [];
        const successfullyDeactivatedTeamIdsSet = new Set<number>(
          payload.reduce((acc: number[], res: PromiseSettledResult<unknown>, index: number) => {
            if (res.status === PromiseAllSettledStatus.Fulfilled) {
              const idAtIndex = idsSource[index];
              if (typeof idAtIndex === 'number') acc.push(idAtIndex);
            }
            return acc;
          }, []),
        );
        if (successfullyDeactivatedTeamIdsSet.size) {
          const teamsInfoMapping = state.teamsInfoMapping;
          const arg: {
            skipStateUpdate?: boolean;
            teamTargets?: Array<{
              teamId: number;
              tenant?: string;
              realmId?: string;
            }>;
            teamNamesById?: Record<number, string>;
          } = meta.arg;
          const targets: Array<{
            teamId: number;
            tenant?: string;
            realmId?: string;
          }> = Array.isArray(arg.teamTargets) ? arg.teamTargets : [];
          const namesById = arg.teamNamesById || {};
          const successfulLines: string[] = [];
          payload.forEach((res: PromiseSettledResult<unknown>, index: number) => {
            if (res.status === PromiseAllSettledStatus.Fulfilled) {
              const t = targets[index];
              if (t) {
                const name = namesById[t.teamId] || teamsInfoMapping[t.teamId]?.name || '';
                const nameSuffix = name ? ` - ${name}` : '';
                const tenantRealm = [t.tenant, t.realmId].filter(Boolean).join(', ');
                const context = tenantRealm ? ` (${tenantRealm})` : '';
                successfulLines.push(`- Team: ${t.teamId}${context}${nameSuffix}`);
              }
            }
          });
          const prompt = successfulLines.join('\n');

          // ✅ FIX: Replace alert() with toast notification
          notificationService.success(`Successfully deactivated teams:${prompt}`, 6000);

          const nextTeamsHistoricalUtilizationState = {
            ...state.teams_historical_utilization,
          };

          const nextTeamsInfoMappingState = state.teamsInfoMapping;

          successfullyDeactivatedTeamIdsSet.forEach((id: number) => {
            delete nextTeamsHistoricalUtilizationState[id];
            delete nextTeamsInfoMappingState[id];
          });

          state.teams = state.teams.filter(
            (team) => !successfullyDeactivatedTeamIdsSet.has(team.id),
          );
          state.teams_historical_utilization = nextTeamsHistoricalUtilizationState;
          state.teamsInfoMapping = nextTeamsInfoMappingState;
        } else {
          // ✅ FIX: Replace alert() with toast notification
          notificationService.error('All teams deactivation failed. Please try again.');
        }
      }
    },
  );

  /* ----------------------- Handle ProfileModule Actions ---------------------- */
  // Handle updateTeamMember from ProfileModule without circular import
  // Note: addMatcher must come after all addCase calls per Redux Toolkit rules
  builder.addMatcher(
    (action): action is any => action.type === 'updateTeamMember/fulfilled',
    (state, { meta, payload }) => {
      const { teamMembershipId } = meta.arg;
      const updatedAccount = payload.team_member?.account;

      if (!updatedAccount) return state;

      state.super_admins = state.super_admins.map((super_admin) =>
        super_admin.id === teamMembershipId
          ? {
              ...super_admin,
              name: updatedAccount.name,
              email: updatedAccount.email,
              first_name: updatedAccount.first_name,
              last_name: updatedAccount.last_name,
            }
          : super_admin,
      );
    },
  );
});
